/**
 * IVR Journey Store
 *
 * Pinia store for managing IVR Journey data
 * Handles caching, loading states, and providing access to journey data
 */

import { defineStore } from "pinia";
import { IVRCallJourney } from "src/models/ivrJourney";
import IVRJourneyService from "src/services/ivrJourneyService";

/**
 * IVR Journey Store State
 */
interface IVRJourneyState {
  // Map of callID -> journey
  journeysByCall: Map<string, IVRCallJourney>;
  // Map of activationID -> array of journeys
  journeysByActivation: Map<number, IVRCallJourney[]>;
  // Loading promises to prevent duplicate requests
  loadingCalls: Map<string, Promise<any>>;
  loadingActivations: Map<number, Promise<any>>;
  // Error state
  error: Error | null;
}

/**
 * Pinia store for IVR Journey data
 */
export const useIVRJourneyStore = defineStore({
  persist: false,
  id: "ivrJourney",
  state: (): IVRJourneyState => ({
    journeysByCall: new Map<string, IVRCallJourney>(),
    journeysByActivation: new Map<number, IVRCallJourney[]>(),
    loadingCalls: new Map<string, Promise<any>>(),
    loadingActivations: new Map<number, Promise<any>>(),
    error: null
  }),

  getters: {
    /**
     * Get journey for a specific call ID
     * @param state - Store state
     * @returns Function that accepts callID and returns journey or undefined
     */
    getJourneyByCallID: (state) => (callID: string): IVRCallJourney | undefined => {
      return state.journeysByCall.get(callID);
    },

    /**
     * Get all journeys for a specific activation ID
     * @param state - Store state
     * @returns Function that accepts activationID and returns array of journeys
     */
    getJourneysByActivationID: (state) => (activationID: number): IVRCallJourney[] => {
      return state.journeysByActivation.get(activationID) || [];
    },

    /**
     * Get all journeys for a case (by patient record ID)
     * This requires looking up activations from the case store
     * For now, returns all journeys we have cached
     * @param state - Store state
     * @returns Function that returns array of all cached journeys
     */
    getAllJourneys: (state) => (): IVRCallJourney[] => {
      return Array.from(state.journeysByCall.values());
    },

    /**
     * Check if a journey is currently loading
     * @param state - Store state
     * @returns Function that accepts callID and returns boolean
     */
    isLoadingJourney: (state) => (callID: string): boolean => {
      return state.loadingCalls.has(callID);
    },

    /**
     * Check if journeys for an activation are currently loading
     * @param state - Store state
     * @returns Function that accepts activationID and returns boolean
     */
    isLoadingActivationJourneys: (state) => (activationID: number): boolean => {
      return state.loadingActivations.has(activationID);
    }
  },

  actions: {
    /**
     * Fetch IVR journey for a specific call ID
     * Prevents duplicate requests by checking loadingCalls map
     * @param callID - UUID of the call
     * @returns Promise that resolves when journey is loaded
     */
    async fetchJourneyByCallID(callID: string): Promise<void> {
      // Check if already loading
      if (this.loadingCalls.has(callID)) {
        return this.loadingCalls.get(callID);
      }

      const service = new IVRJourneyService();
      const promise = service.fetchJourneyByCallID(callID);
      this.loadingCalls.set(callID, promise);

      try {
        const journeys = await promise;

        // Store all journeys returned
        journeys.forEach(journey => {
          this.locallySaveJourney(journey);
        });

        this.error = null;
      } catch (error) {
        console.error("Error fetching IVR journey by call ID:", error);
        this.error = error instanceof Error ? error : new Error(String(error));
        throw error;
      } finally {
        this.loadingCalls.delete(callID);
      }
    },

    /**
     * Fetch all IVR journeys for a specific activation ID
     * Prevents duplicate requests by checking loadingActivations map
     * @param activationID - Numeric ID of the activation
     * @returns Promise that resolves when journeys are loaded
     */
    async fetchJourneysByActivationID(activationID: number): Promise<void> {
      // Check if already loading
      if (this.loadingActivations.has(activationID)) {
        return this.loadingActivations.get(activationID);
      }

      const service = new IVRJourneyService();
      const promise = service.fetchJourneysByActivationID(activationID);
      this.loadingActivations.set(activationID, promise);

      try {
        const journeys = await promise;

        // Store journeys by activation ID
        this.journeysByActivation.set(activationID, journeys);

        // Also store each journey individually by call ID
        journeys.forEach(journey => {
          this.journeysByCall.set(journey.call.id, journey);
        });

        this.error = null;
      } catch (error) {
        console.error("Error fetching IVR journeys by activation ID:", error);
        this.error = error instanceof Error ? error : new Error(String(error));
        throw error;
      } finally {
        this.loadingActivations.delete(activationID);
      }
    },

    /**
     * Locally save a journey to the store
     * Updates both journeysByCall and journeysByActivation maps
     * @param journey - Journey object to save
     */
    locallySaveJourney(journey: IVRCallJourney): void {
      // Store by call ID
      this.journeysByCall.set(journey.call.id, journey);

      // Store by activation ID if present
      if (journey.call.activationID !== null) {
        const existing = this.journeysByActivation.get(journey.call.activationID) || [];

        // Remove any existing journey with the same call ID
        const filtered = existing.filter(j => j.call.id !== journey.call.id);

        // Add the new journey
        filtered.push(journey);

        this.journeysByActivation.set(journey.call.activationID, filtered);
      }
    },

    /**
     * Clear all journey data from the store
     */
    clearJourneys(): void {
      this.journeysByCall.clear();
      this.journeysByActivation.clear();
      this.loadingCalls.clear();
      this.loadingActivations.clear();
      this.error = null;
    },

    /**
     * Clear journeys for a specific activation
     * @param activationID - Activation ID to clear
     */
    clearJourneysForActivation(activationID: number): void {
      // Get journeys for this activation
      const journeys = this.journeysByActivation.get(activationID) || [];

      // Remove each journey from journeysByCall
      journeys.forEach(journey => {
        this.journeysByCall.delete(journey.call.id);
      });

      // Remove from journeysByActivation
      this.journeysByActivation.delete(activationID);
    }
  }
});
